import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cn } from "@repo/blacksmith-ui/cn";
import { HOST_SUFFIX_TO_PROVIDER, KNOWN_MCP_PROVIDERS, PROVIDER_BRANDS, type McpProviderBrandContext } from "./mcpProviders";
interface McpProviderIconProps {
  providerId?: string | null;
  // When `providerId` is missing, the component falls back to inferring
  // a provider from this URL's host (see inferProviderFromHost). Lets
  // unclassified custom servers still render brand icons when their URL
  // matches a known cloud host.
  url?: string | null;
  displayName?: string | null;
  /**
   * Pixel size of the brand glyph rendered inside the 24px chip. Default
   * 14 leaves the standard padding; bump for chips that need more visual
   * weight (e.g. the larger Recommended provider rows).
   */
  glyphSize?: number;
  /**
   * Brand style context: providers can override bg/fg per context.
   * Use `toolcall` for neutral 14px glyphs in tool-call rows.
   */
  variant?: McpProviderBrandContext;
  className?: string;
}

/**
 * Try to infer a known provider slug from a URL or bare host. Returns
 * `null` when nothing matches. Used as a fallback when an MCP server has
 * no explicit `provider_id` set — e.g. a custom server the user added
 * manually that happens to point at the official Sentry cloud.
 *
 * The match is suffix-based ("mcp.sentry.dev" matches ".sentry.dev")
 * with a strict allowlist; arbitrary domains never resolve to a brand.
 */
export function inferProviderFromHost(urlOrHost: string | null | undefined): string | null {
  if (!urlOrHost) return null;
  const trimmed = urlOrHost.trim();
  if (!trimmed) return null;
  let host: string;
  try {
    host = new URL(trimmed).hostname.toLowerCase();
  } catch {
    // Caller may have passed a bare host already (no scheme). Accept it
    // as long as it doesn't contain spaces or path separators.
    if (/[\s/?#]/.test(trimmed)) return null;
    host = trimmed.toLowerCase();
  }
  if (!host) return null;
  for (const [suffix, provider] of HOST_SUFFIX_TO_PROVIDER) {
    if (host === suffix.slice(1) || host.endsWith(suffix)) return provider;
  }
  return null;
}
function fallbackLetter(name?: string | null, providerId?: string | null): string {
  const source = name?.trim() || providerId?.trim();
  if (!source) return "?";
  return source[0].toUpperCase();
}

/**
 * Renders a 24px provider icon for an MCP server. Known provider IDs render
 * the official brand glyph from Simple Icons; unknown providers fall back
 * to a square chip with the first letter of the display name.
 */
export function McpProviderIcon(t0) {
  const $ = _c(34);
  const {
    providerId,
    url,
    displayName,
    glyphSize: t1,
    variant: t2,
    className
  } = t0;
  const glyphSize = t1 === undefined ? 14 : t1;
  const variant = t2 === undefined ? "default" : t2;
  const explicitKey = providerId?.toLowerCase().trim();
  const inferredKey = explicitKey ? null : inferProviderFromHost(url);
  const key = explicitKey || inferredKey || undefined;
  const brand = key ? PROVIDER_BRANDS[key] : undefined;
  if (variant === "toolcall") {
    if (brand) {
      const {
        Icon
      } = brand;
      let t3;
      if ($[0] !== className) {
        t3 = cn("inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center", className);
        $[0] = className;
        $[1] = t3;
      } else {
        t3 = $[1];
      }
      let t4;
      if ($[2] !== Icon || $[3] !== glyphSize) {
        t4 = <Icon size={glyphSize} className="fill-current" />;
        $[2] = Icon;
        $[3] = glyphSize;
        $[4] = t4;
      } else {
        t4 = $[4];
      }
      let t5;
      if ($[5] !== t3 || $[6] !== t4) {
        t5 = <span className={t3} aria-hidden={true}>{t4}</span>;
        $[5] = t3;
        $[6] = t4;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      return t5;
    }
    let t3;
    if ($[8] !== className) {
      t3 = cn("inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm text-[10px] font-semibold", className);
      $[8] = className;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== displayName || $[11] !== providerId) {
      t4 = fallbackLetter(displayName, providerId);
      $[10] = displayName;
      $[11] = providerId;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    let t5;
    if ($[13] !== t3 || $[14] !== t4) {
      t5 = <span className={t3} aria-hidden={true}>{t4}</span>;
      $[13] = t3;
      $[14] = t4;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    return t5;
  }
  if (brand) {
    const {
      Icon: Icon_0
    } = brand;
    const override = variant !== "default" ? brand[variant] : undefined;
    const bg = override?.bg ?? brand.bg;
    const fg = override?.fg ?? brand.fg;
    let t3;
    if ($[16] !== bg || $[17] !== className || $[18] !== fg) {
      t3 = cn("inline-flex items-center justify-center rounded-md size-6 shrink-0", bg, fg, className);
      $[16] = bg;
      $[17] = className;
      $[18] = fg;
      $[19] = t3;
    } else {
      t3 = $[19];
    }
    let t4;
    if ($[20] !== Icon_0 || $[21] !== glyphSize) {
      t4 = <Icon_0 size={glyphSize} className="fill-current" />;
      $[20] = Icon_0;
      $[21] = glyphSize;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    let t5;
    if ($[23] !== t3 || $[24] !== t4) {
      t5 = <span className={t3}>{t4}</span>;
      $[23] = t3;
      $[24] = t4;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    return t5;
  }
  let t3;
  if ($[26] !== className) {
    t3 = cn("inline-flex items-center justify-center rounded-md size-6 shrink-0 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 text-xs font-semibold", className);
    $[26] = className;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[28] !== displayName || $[29] !== providerId) {
    t4 = fallbackLetter(displayName, providerId);
    $[28] = displayName;
    $[29] = providerId;
    $[30] = t4;
  } else {
    t4 = $[30];
  }
  let t5;
  if ($[31] !== t3 || $[32] !== t4) {
    t5 = <span className={t3} aria-hidden="true">{t4}</span>;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
  } else {
    t5 = $[33];
  }
  return t5;
}

// Re-exported for callers that want the slug list (e.g. registry-drift
// tests). Source of truth lives in ./mcpProviders.
export { KNOWN_MCP_PROVIDERS };